/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC } from 'react';
import { Stack, Skeleton, SkeletonProps } from '@mui/material';

import TestIds from '../../../../testIds';
import { TProcessedCostPrices } from './CostPriceSuccess';

export interface ICostPriceLoadingProps {
  processedCostPrices?: TProcessedCostPrices[] | null;
}

const CostPriceLoading: FC<ICostPriceLoadingProps> = ({
  processedCostPrices,
}: ICostPriceLoadingProps): JSX.Element => {
  const WaveSkeleton = (props: SkeletonProps): JSX.Element => (
    <Skeleton variant='farmlands2.0_default' animation='wave' height={12} {...props} />
  );

  const BreakpointStack = (): JSX.Element => (
    <Stack spacing={2} direction='row'>
      <WaveSkeleton width={65} />
      <WaveSkeleton width={50} />
    </Stack>
  );

  const defaultWidths: number[] = [268, 144, 256];

  const siteContainer = (siteIndex: number, siteName?: string, breakpointCount = 1): JSX.Element => (
    <Stack
      key={siteIndex}
      spacing='0.5rem'
      data-testid={TestIds.feat.costPrice.loading.siteContainer}
    >
      {siteName ? (
        <WaveSkeleton>{siteName}</WaveSkeleton>
      ) : (
        <WaveSkeleton width={defaultWidths[siteIndex]} />
      )}

      {Array.from({ length: breakpointCount }, (_, breakpointIndex: number) => (
        <BreakpointStack key={breakpointIndex} />
      ))}
    </Stack>
  );

  return (
    <Stack spacing='1.5rem' data-testid={TestIds.feat.costPrice.loading.component}>
      {processedCostPrices?.length
        ? processedCostPrices.map(([siteName, siteBreaks], siteIndex) =>
            siteContainer(siteIndex, siteName, siteBreaks.length),
          )
        : Array.from({ length: 3 }, (_, siteIndex) => siteContainer(siteIndex))}
    </Stack>
  );
};

export default CostPriceLoading;
